import { AIRPORTS, REGIONAL_AIRSPACE, API_CONFIG } from "./constants";
import { findAirlineByCode } from "./airlineRegistry";

/**
 * Retry a fetch with exponential backoff on 5xx errors.
 */
const fetchWithRetry = async (url, options = {}, retries = 2) => {
  for (let i = 0; i <= retries; i++) {
    try {
      const response = await fetch(url, options);
      if (response.ok || response.status < 500) return response;
      if (i < retries) await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
    } catch (err) {
      if (i === retries) throw err;
      await new Promise((r) => setTimeout(r, 1000 * (i + 1)));
    }
  }
  return fetch(url, options);
};

/**
 * Get airport data by hub code
 * @param {string} hubCode - The hub code (e.g., "Ghana (ACC)")
 * @returns {Object} Airport object or Regional Airspace object
 */
export const getAirportByHubCode = (hubCode) => {
  if (hubCode === REGIONAL_AIRSPACE.hubCode) {
    return REGIONAL_AIRSPACE;
  }
  return AIRPORTS.find((airport) => airport.hubCode === hubCode);
};

/**
 * Format temperature based on language
 * @param {number} fahrenheit - Temperature in Fahrenheit
 * @param {string} language - Language code ('en' or 'fr')
 * @returns {string} Formatted temperature string
 */
export const formatTemperature = (fahrenheit, language) => {
  if (language === "fr") {
    const celsius = Math.round(((fahrenheit - 32) * 5) / 9);
    return `${celsius}°C`;
  }
  return `${fahrenheit}°F`;
};

/**
 * Get current GMT time formatted as HH:MM GMT
 * @returns {string} Formatted time string
 */
export const getCurrentGMTTime = () => {
  const now = new Date();
  const hours = now.getUTCHours().toString().padStart(2, "0");
  const minutes = now.getUTCMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes} GMT`;
};

/**
 * Fetch weather data from RenuAirHub backend API
 * @param {string} airportCode - IATA airport code (e.g., 'ACC', 'LOS')
 * @returns {Promise<Object>} Weather data object
 */
export const fetchWeatherFromAPI = async (airportCode) => {
  // No refresh param — use backend default (1800s). The weather-refresh Lambda
  // pre-warms all flight airports every 30 min, so requests are cache hits.
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WEATHER}/${airportCode}`;

  const response = await fetchWithRetry(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch weather for ${airportCode}`);
  }

  return await response.json();
};

/**
 * Convert Fahrenheit to Celsius
 * @param {number} fahrenheit - Temperature in Fahrenheit
 * @returns {number} Temperature in Celsius
 */
export const fahrenheitToCelsius = (fahrenheit) => {
  return Math.round(((fahrenheit - 32) * 5) / 9);
};

/**
 * Get weather icon component based on condition string
 * @param {string} condition - Weather condition from API (e.g., "Sunny", "Partly cloudy", "Rainy")
 * @returns {string} Icon name for MUI icon
 */
export const getWeatherIconName = (condition) => {
  const conditionLower = condition?.toLowerCase() || "";

  if (conditionLower.includes("sunny") || conditionLower.includes("clear")) {
    return "WbSunny";
  } else if (
    conditionLower.includes("partly cloudy") ||
    conditionLower.includes("partly")
  ) {
    return "WbCloudy";
  } else if (
    conditionLower.includes("cloudy") ||
    conditionLower.includes("overcast")
  ) {
    return "Cloud";
  } else if (
    conditionLower.includes("rain") ||
    conditionLower.includes("drizzle") ||
    conditionLower.includes("shower")
  ) {
    return "Umbrella";
  } else if (
    conditionLower.includes("storm") ||
    conditionLower.includes("thunder")
  ) {
    return "Thunderstorm";
  } else if (conditionLower.includes("snow")) {
    return "AcUnit";
  } else if (
    conditionLower.includes("fog") ||
    conditionLower.includes("mist")
  ) {
    return "Foggy";
  }

  // Default to cloudy
  return "Cloud";
};

/**
 * Get weather icon color based on condition
 * @param {string} condition - Weather condition from API
 * @returns {string} Hex color code
 */
export const getWeatherIconColor = (condition) => {
  const conditionLower = condition?.toLowerCase() || "";

  if (conditionLower.includes("sunny") || conditionLower.includes("clear")) {
    return "#ff9800"; // Orange
  } else if (
    conditionLower.includes("rain") ||
    conditionLower.includes("storm")
  ) {
    return "#2196f3"; // Blue
  } else if (conditionLower.includes("snow")) {
    return "#b3e5fc"; // Light blue
  } else if (conditionLower.includes("fog")) {
    return "#9e9e9e"; // Gray
  }

  // Default cloudy color
  return "#90a4ae"; // Blue gray
};

/**
 * Fetch flights data from RenuAirHub backend API (FlightAware AeroAPI)
 * Reads from pre-populated cache by default (background refresh every 35 min).
 * @param {string} airportCode - IATA airport code (e.g., 'ACC', 'CKY', 'ABJ', 'LOS', 'DSS')
 * @param {string} type - Flight type ('departure' or 'arrival')
 * @param {boolean} forceRefresh - Set true to bypass cache and fetch live data
 * @returns {Promise<Object>} Flights data object
 */
export const fetchFlightsFromAPI = async (
  airportCode,
  type,
  forceRefresh = false,
) => {
  let url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FLIGHTS}/${airportCode}?type=${type}`;
  if (forceRefresh) {
    url += `&forceRefresh=true`;
  }

  const response = await fetchWithRetry(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch ${type} flights for ${airportCode}`);
  }

  return await response.json();
};

/**
 * Fetch airport data from RenuAirHub backend API
 * @param {string|string[]} codes - Single IATA code or array of IATA codes
 * @returns {Promise<Object>} Airport data object with airports array
 */
export const fetchAirportsFromAPI = async (codes) => {
  let url;

  if (Array.isArray(codes)) {
    // Multiple airports
    const codesParam = codes.join(",");
    url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AIRPORTS}?codes=${codesParam}`;
  } else {
    // Single airport
    url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AIRPORTS}?code=${codes}`;
  }

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch airport data for ${codes}`);
  }

  return await response.json();
};

/**
 * Calculate Levenshtein distance between two strings (for fuzzy matching)
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Edit distance between the strings
 */
export const levenshteinDistance = (str1, str2) => {
  const s1 = str1.toLowerCase();
  const s2 = str2.toLowerCase();

  const len1 = s1.length;
  const len2 = s2.length;

  // Create a 2D array for dynamic programming
  const dp = Array(len1 + 1)
    .fill(null)
    .map(() => Array(len2 + 1).fill(0));

  // Initialize first row and column
  for (let i = 0; i <= len1; i++) dp[i][0] = i;
  for (let j = 0; j <= len2; j++) dp[0][j] = j;

  // Fill the dp table
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (s1[i - 1] === s2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1, // deletion
          dp[i][j - 1] + 1, // insertion
          dp[i - 1][j - 1] + 1, // substitution
        );
      }
    }
  }

  return dp[len1][len2];
};

/**
 * Check if two strings are similar using fuzzy matching
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @param {number} threshold - Maximum allowed distance (default: 2)
 * @returns {boolean} True if strings are similar
 */
export const isFuzzyMatch = (str1, str2, threshold = 2) => {
  if (!str1 || !str2) return false;

  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  // For short strings, use absolute threshold
  if (maxLength <= 5) {
    return distance <= 1;
  }

  // For longer strings, use relative threshold
  return distance <= threshold;
};

/**
 * Store enriched flight records to the backend API
 * @param {string} airportCode - IATA airport code (e.g., "ABJ", "CKY")
 * @param {string} flightType - "departure" or "arrival"
 * @param {Array} enrichedFlights - Array of enriched flight objects from the UI
 * @returns {Promise<Object>} API response
 */
export const storeFlightRecords = async (
  airportCode,
  flightType,
  enrichedFlights,
) => {
  if (!enrichedFlights || enrichedFlights.length === 0) return null;

  const flights = enrichedFlights
    .filter((f) => f.flight && f.flight !== "N/A")
    .map((f) => {
      const originMatch = f.origin?.match(/\(([A-Z]{3})\)/);
      const destMatch = f.destination?.match(/\(([A-Z]{3})\)/);
      const originCode = originMatch ? originMatch[1] : "";
      const destCode = destMatch ? destMatch[1] : "";

      const originAirport = f.origin?.match(/^(.+?)\s*\(/)?.[1] || f.origin;
      const destAirport =
        f.destination?.match(/^(.+?)\s*\(/)?.[1] || f.destination;

      const codeSharedAirlines = {};
      if (f.codeshares && f.codeshares.length > 0) {
        f.codeshares.forEach((cs) => {
          if (cs.flight) {
            // Try registry first, then resolve from IATA prefix, fallback to code
            let name = cs.airline;
            if (!name || name === "—") {
              const iataPrefix = cs.flight.slice(0, 2);
              const resolved = findAirlineByCode(iataPrefix);
              name = resolved?.name || iataPrefix;
            }
            codeSharedAirlines[cs.flight] = name;
          }
        });
      }

      const formatDelay = (mins) => {
        if (!mins || mins <= 0) return null;
        if (mins >= 60) {
          const h = Math.floor(mins / 60);
          const m = mins % 60;
          return m === 0 ? `${h}h` : `${h}h${m}min`;
        }
        return `${mins}min`;
      };
      const originDelay = formatDelay(f.departureDelayMinutes);
      const destDelay = formatDelay(f.arrivalDelayMinutes);

      return {
        flightIata: f.flight,
        status: f.statusKey || f.status,
        airline: f.airline,
        codeSharedAirlines,
        origin: {
          airport: `${originAirport} (${originCode})`,
          country: f.origin?.split(" - ").pop() || "",
          scheduled: f._rawDepartureScheduled || "",
          terminal: f.departureTerminal || null,
          delay: originDelay,
        },
        destination: {
          airport: `${destAirport} (${destCode})`,
          country: f.destination?.split(" - ").pop() || "",
          scheduled: f._rawArrivalScheduled || "",
          terminal: f.arrivalTerminal || null,
          delay: destDelay,
        },
        duration: f.duration || "",
      };
    });

  if (flights.length === 0) return null;

  try {
    const response = await fetch(
      `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FLIGHT_RECORDS}/${airportCode}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ airportCode, flightType, flights }),
      },
    );

    // Retry once on 500 (Lambda cold start)
    if (response.status >= 500) {
      await new Promise((r) => setTimeout(r, 1000));
      const retry = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FLIGHT_RECORDS}/${airportCode}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ airportCode, flightType, flights }),
        },
      );
      const data = await retry.json();
      if (!retry.ok) return null;
      return data;
    }

    const data = await response.json();

    if (!response.ok) return null;
    return data;
  } catch (error) {
    console.error("Error storing flight records:", error);
    return null;
  }
};
